import { useState } from 'react';
import { withFormik, WithFormikConfig } from 'formik';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import * as Yup from 'yup';
import { errorMessage } from '@firsttable/functions';
import { useConsent } from '@/lib/consent';
import Logger from '@/lib/logger';
import { applySignupAttribution } from '@context/SignupAttribution';
import usePD6990ReplaceSignUpContactPermissionWithCheckboxExperiment from '@/components/analytics/experiments/usePD6990ReplaceSignUpContactPermissionWithCheckboxExperiment';
import useRecaptchaEnabled from '@/components/analytics/featureFlags/recaptcha';
import {
  contactPermissionCheckboxFields,
  isContactPermissionRadio,
} from '@/components/organisms/Forms/contactPermissionCheckbox';
import { SUBURB_IDS_UNAVAILABLE_FIELD } from '@/components/organisms/Forms/Fields/SuburbIdsField';

interface withCypress extends Window {
  Cypress?: any;
}

declare let window: withCypress;

const niceFieldType = (type: string) => type.toLowerCase().replace('_', '-');
const checkFieldType = (type: string, match: string | [string]): boolean =>
  [].concat(match).map(niceFieldType).includes(niceFieldType(type));
const isRecaptchaField = (field) => checkFieldType(field.type, 'recaptcha');

// A hidden reCAPTCHA stays in the initial values, so enableReinitialize doesn't wipe in-progress input when the flag resolves.
const visibleFormFields = (formFields, recaptchaEnabled: boolean) =>
  recaptchaEnabled ? formFields : formFields?.filter((field) => !isRecaptchaField(field));

// The contact-form API validates every spam field named in the submitted JSON, including an empty token.
const withoutHiddenRecaptchaValues = (values, formFields, recaptchaEnabled: boolean) => {
  if (recaptchaEnabled !== false || !values) {
    return values;
  }

  const hiddenNames = new Set(
    (formFields ?? []).filter(isRecaptchaField).map((field) => field.name)
  );
  if (hiddenNames.size === 0) {
    return values;
  }

  return Object.fromEntries(Object.entries(values).filter(([name]) => !hiddenNames.has(name)));
};

// Move these to a common file
export const nameToCamelCase = (name: string) => name[0].toLowerCase() + name.substring(1);

// Array.map for objects
export const objectMap = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

// Array.map object keys
export const objectKeysMap = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [fn(k, i), v]));

const logger = new Logger('withDynamicFormik');
// logger.enable();

const trackSuburbSelectorSubmitted = ({ formFields, track, values, cityId }) => {
  if (
    typeof track !== 'function' ||
    !(values.cityId ?? cityId) ||
    !formFields?.some((field) => field.name === 'suburbIds') ||
    values[SUBURB_IDS_UNAVAILABLE_FIELD] !== false
  ) {
    return;
  }

  const suburbIds = Array.isArray(values.suburbIds) ? values.suburbIds : [];

  try {
    track('suburb_selector_submitted', {
      suburbs_selected_count: suburbIds.length,
      suburb_skipped: suburbIds.length === 0,
    });
  } catch (error) {
    logger.error('Failed to track suburb selector submission', error);
  }
};

function ContactPermissionExperimentReader({ onChange }: { onChange: (enabled: boolean) => void }) {
  const showCheckbox = usePD6990ReplaceSignUpContactPermissionWithCheckboxExperiment();

  // Layout effect so the checkbox's default applies before the first paint.
  useIsomorphicLayoutEffect(() => {
    onChange(showCheckbox);
  }, [onChange, showCheckbox]);

  return null;
}

function RecaptchaFlagReader({ onChange }: { onChange: (enabled: boolean) => void }) {
  const recaptchaEnabled = useRecaptchaEnabled();

  useIsomorphicLayoutEffect(() => {
    onChange(recaptchaEnabled);
  }, [onChange, recaptchaEnabled]);

  return null;
}

const withDynamicFormik = (
  config: Partial<WithFormikConfig<any>> & { extraInitialValues?: [key: string] } = {}
) => {
  const mapPropsToValues = (props) => {
    const initialValues = {
      ...props?.extraInitialValues,
      ...config?.extraInitialValues,
    };
    const fields = [...(props?.formFields || [])];
    // eslint-disable-next-line array-callback-return
    fields?.map((node) => {
      const { name } = node;
      let value = null;
      if (name === 'suburbIds') {
        value = Array.isArray(node?.value) ? node.value.map((id) => Number(id)) : [];
      } else if (checkFieldType(node.type, 'checkbox-group') && node.options.length) {
        value = node.options.filter((option) => option.selected).map((option) => option.value);
      } else if (checkFieldType(node.type, 'radio-group')) {
        value = node.options.find((option) => option.selected)?.value || node.value;
      } else if (checkFieldType(node.type, 'checkbox')) {
        value = parseInt(node.value) === 1;
      } else if (checkFieldType(node.type, 'recaptcha')) {
        value = '';
      } else {
        value = node?.value || '';
      }
      initialValues[name] = value || initialValues[name] || value;
    });

    logger.log('initialValues', initialValues);
    return initialValues;
  };

  const validationSchema = (props) => {
    const toValidate = {};
    visibleFormFields(props.formFields, props.recaptchaEnabled)?.map((node) => {
      const { name } = node;

      if (node.required) {
        if (name === 'suburbIds') {
          toValidate[name] = Yup.array()
            .of(Yup.number())
            .test(
              'required-when-suburbs-available',
              'Please select at least one suburb',
              function validateSuburbIds(value) {
                const cityId = this.parent?.cityId;
                if (
                  cityId == null ||
                  cityId === '' ||
                  this.parent?.[SUBURB_IDS_UNAVAILABLE_FIELD]
                ) {
                  return true;
                }
                return Array.isArray(value) && value.length > 0;
              }
            );
          return true;
        }

        if (checkFieldType(node.type, 'email')) {
          toValidate[name] = Yup.string().email('Invalid email').required('This field is required');
        } else if (checkFieldType(node.type, 'checkbox')) {
          toValidate[name] = Yup.boolean().oneOf([true], `This field must be checked`).required();
        } else if (checkFieldType(node.type, 'radio-group')) {
          toValidate[name] = Yup.string()
            .nullable() // nullable but actually required
            .oneOf(
              node.options.map((option) => option.value),
              'Please select an option'
            );
        } else {
          toValidate[name] = Yup.string().noHtml().required(`'${node.title}' is a required field`);
          const min = node.attributes?.find(({ key }) => key === 'minlength')?.value;
          if (min) {
            toValidate[name] = toValidate[name].min(min, `Must be at least ${min} characters`);
          }
        }
      }
      if (checkFieldType(node.type, 'recaptcha') && !window?.Cypress) {
        toValidate[name] = Yup.string().nullable().required("Please confirm you're not a bot");
      }

      return true;
    });

    return Yup.object().shape({
      ...toValidate,
      ...props?.extraValidation,
    });
  };

  const handleErrorDefault = (message, { setFormStatus, ...rest }) => {
    setFormStatus({ message, type: 'danger' });
  };

  const handleSubmit = (
    values,
    {
      setSubmitting,
      setStatus: setFormStatus,
      resetForm,
      props: {
        formData,
        hideModal,
        transformInput,
        transformResponse,
        submitAction,
        complete,
        handleError = handleErrorDefault,
        formFields,
        track,
        signupAttribution,
        recaptchaEnabled,
        ...rest
      },
    }
  ) => {
    const submittedValues = withoutHiddenRecaptchaValues(values, formFields, recaptchaEnabled);
    submitAction({
      variables: applySignupAttribution(
        transformInput(submittedValues, {
          resetForm,
          setFormStatus,
          formData,
          formFields,
          ...rest,
        }),
        signupAttribution,
        { surface: 'dynamic form' }
      ),
    })
      .then(({ data }) => {
        complete(
          { ...transformResponse(data) },
          {
            resetForm,
            setFormStatus,
            formData,
            values,
            track,
            ...rest,
          }
        );

        trackSuburbSelectorSubmitted({
          formFields,
          track,
          values,
          cityId: rest.cityId,
        });
      })
      .catch((e) => {
        handleError(errorMessage(e), { setFormStatus, values, ...rest });
        logger.error(e);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const formik = withFormik({
    enableReinitialize: true,
    mapPropsToValues,
    validationSchema,
    handleSubmit,
    ...config,
  });

  return (Component) => {
    const VisibleFieldsForm = ({ formFields, recaptchaEnabled, ...props }) => (
      <Component {...props} formFields={visibleFormFields(formFields, recaptchaEnabled)} />
    );
    const FormikForm = formik(VisibleFieldsForm);

    const DynamicFormikForm = ({ formFields, ...props }) => {
      const [showCheckbox, setShowCheckbox] = useState(false);
      const [recaptchaEnabled, setRecaptchaEnabled] = useState(true);
      const { isGdprRegion } = useConsent();
      const hasContactPermissionRadio = formFields?.some(isContactPermissionRadio);
      const hasRecaptchaField = formFields?.some(isRecaptchaField);

      // Only forms that render the contact-permission radio or reCAPTCHA should read those experiments. The reads are siblings, not wrappers, because FormikForm must keep its tree position when the fields arrive: remounting it restarts the email check, which then clears the sign-up fields.
      return (
        <>
          {hasContactPermissionRadio && (
            <ContactPermissionExperimentReader onChange={setShowCheckbox} />
          )}
          {hasRecaptchaField && <RecaptchaFlagReader onChange={setRecaptchaEnabled} />}
          <FormikForm
            {...props}
            recaptchaEnabled={recaptchaEnabled}
            formFields={
              hasContactPermissionRadio && showCheckbox
                ? contactPermissionCheckboxFields(formFields, !isGdprRegion)
                : formFields
            }
          />
        </>
      );
    };

    DynamicFormikForm.displayName = 'DynamicFormikForm';

    return DynamicFormikForm;
  };
};

export default withDynamicFormik;
